<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { usePvpCardGameStore } from '../services/gameService';
import { formatNumberWithCommas } from '@/utils';
import SoulCurrency from './SoulCurrency.vue';
import { useUserStore } from '@/stores/modules/user';

const store = usePvpCardGameStore();
const userStore = useUserStore();

// 显示状态
const isVisible = computed(() => store.pveBattleResult !== null);
const result = computed(() => store.pveBattleResult!);

// 卡牌详情
const cardPackData = computed(() => {
    if (!result.value?.cards) return [];
    return result.value.cards.map(([id, count]) => ({
        ...store.getDetailById(id),
        count,
    }));
});

// 动画控制
const showDetails = ref(false);
let autoCloseTimer: number | null = null;

// 自动关闭 & 动画触发
watch(() => store.pveBattleResult, (newVal) => {
    if (newVal) {
        showDetails.value = false;
        setTimeout(() => {
            showDetails.value = true;
            // 发送全服通知（稀有卡）
            cardPackData.value.forEach(item => {
                if (item.id > 4000) {
                    store.sendNotice(`恭喜玩家[${userStore.nickname}]获得${item.count}张${item.name}！`);
                }
            });
        }, 800);
        autoCloseTimer = window.setTimeout(handleClose, 10000);
    } else {
        if (autoCloseTimer) clearTimeout(autoCloseTimer);
    }
});

function handleClose() {
    store.clearPveBattleResult();
}
</script>

<template>
    <Teleport to="body">
        <div v-if="isVisible" class="battle-result-overlay" @click="handleClose">
            <div class="result-container">
                <!-- 标题：通关提示 -->
                <div class="result-title pve">
                    {{ result.reason || '战斗结束' }}
                </div>

                <!-- 奖励详情 -->
                <transition name="fade-slide">
                    <div v-if="showDetails" class="result-details">
                        <!-- 魂尘 -->
                        <div v-if="result.coin" class="reward-item">
                            <SoulCurrency type="dust" size="20px" />
                            魂尘：{{ formatNumberWithCommas(result.coin) }}
                        </div>

                        <!-- 魂晶（可选） -->
                        <div v-if="result.diamond" class="reward-item">
                            <SoulCurrency type="quartz" size="20px" />
                            魂晶：{{ formatNumberWithCommas(result.diamond) }}
                        </div>

                        <!-- 卡牌奖励 -->
                        <div v-if="cardPackData.length" class="card-rewards">
                            <div v-for="item in cardPackData" :key="item.id" class="card-item">
                                <span class="card-name" :class="item.grade">
                                    {{ item.name }} ×{{ item.count }}
                                </span>
                            </div>
                        </div>
                    </div>
                </transition>

                <!-- 跳过提示 -->
                <div class="skip-hint">点击任意位置继续</div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped lang="scss">
.battle-result-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    animation: zoomIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.result-container {
    text-align: center;
    color: white;
    padding: 30px;
    max-width: 90%;
    max-height: 80vh;
    overflow-y: auto;
}

.result-title {
    font-size: 36px;
    font-weight: bold;
    margin-bottom: 24px;
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.8);
    color: #FFD700;
    /* 金色 */
    line-height: 1.3;
}

.reward-item {
    font-size: 20px;
    margin: 12px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.card-rewards {
    margin-top: 20px;
}

.card-item {
    margin: 8px 0;
}

.card-name {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.15);
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.9;
    }

    &.common {
        color: #aaa;
    }

    &.rare {
        color: #4caf50;
    }

    &.epic {
        color: #2196f3;
    }

    &.legendary {
        color: #ff9800;
    }

    &.mythic {
        color: #e91e63;
    }
}

.skip-hint {
    margin-top: 30px;
    font-size: 14px;
    color: #aaa;
    animation: pulse 1.5s infinite;
}

/* 动画 */
@keyframes zoomIn {
    from {
        transform: scale(0.8);
        opacity: 0;
    }

    to {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes pulse {

    0%,
    100% {
        opacity: 0.4;
    }

    50% {
        opacity: 1;
    }
}

.fade-slide-enter-active {
    transition: all 0.5s ease 0.3s;
}

.fade-slide-enter-from {
    opacity: 0;
    transform: translateY(20px);
}

.fade-slide-enter-to {
    opacity: 1;
    transform: translateY(0);
}
</style>